/**
 * Vorlage „Gehen lassen“.
 *
 * Hefeteig in einer Schüssel geht langsam auf, bis er sein Volumen etwa
 * verdoppelt hat. Zwei gestrichelte Marken zeigen Anfang und Ziel. Auf Wunsch
 * liegt ein Tuch über der Schüssel und wölbt sich mit dem Teig.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path } from '../paths.ts';
import { compact, faded, group, line, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { arrow, BADGE_RIGHT, counter, doneMark, durationLabel, timerBadge } from '../parts/stage.ts';
import { bowl, interiorClip } from '../parts/vessels.ts';
import { minutesFromText } from '../inference.ts';
import { booleanValue, defineTemplate, numberValue } from '../template.ts';

const BOWL = { cx: 104, rim: 84, width: 128, height: 50 } as const;
/** Oberkante des Teigs am Anfang und nach dem Gehen. */
const START_TOP = BOWL.rim - 2;
const END_TOP = BOWL.rim - 30;

/** Zeitplan. */
const TIME = { rise: [0.06, 0.78], done: [0.8, 0.84], reset: [0.9, 1] } as const;

/** Teig als gewölbte Masse mit Oberkante top. */
function doughTop(top: number): string {
  const rx = BOWL.width / 2 - 3;
  return path()
    .move(BOWL.cx - rx, BOWL.rim + 14)
    .curve(BOWL.cx - rx * 0.9, top - 2, BOWL.cx + rx * 0.9, top - 2, BOWL.cx + rx, BOWL.rim + 14)
    .close()
    .toString();
}

/** Tuch über der Schüssel; wölbt sich über dem Teig. */
function cloth(top: number): SceneNode[] {
  const rx = BOWL.width / 2 + 6;
  const peak = Math.min(top - 4, BOWL.rim - 12);
  const sheet = path()
    .move(BOWL.cx - rx, BOWL.rim + 16)
    .quad(BOWL.cx - rx - 2, BOWL.rim - 2, BOWL.cx - rx * 0.6, peak + 6)
    .quad(BOWL.cx, peak - 6, BOWL.cx + rx * 0.6, peak + 6)
    .quad(BOWL.cx + rx + 2, BOWL.rim - 2, BOWL.cx + rx, BOWL.rim + 16)
    .quad(BOWL.cx + rx * 0.5, BOWL.rim + 10, BOWL.cx, BOWL.rim + 14)
    .quad(BOWL.cx - rx * 0.5, BOWL.rim + 18, BOWL.cx - rx, BOWL.rim + 16)
    .close()
    .toString();
  const stripes: SceneNode[] = [-0.45, -0.3, 0.3, 0.45].map((f) =>
    line(BOWL.cx + rx * f, peak - 10, BOWL.cx + rx * f, BOWL.rim + 20, {
      stroke: 'shade',
      strokeWidth: STROKE.outline,
    }),
  );
  return [
    shape(sheet, styles.flat('paper')),
    group(stripes, { clip: sheet }),
    shape(sheet, styles.outline()),
  ];
}

/** Vorlage „Gehen lassen“. */
export const bakeProofTemplate = defineTemplate({
  id: 'bake.proof',
  group: 'bake',
  title: 'Gehen lassen',
  description: 'Der Hefeteig ruht an einem warmen Ort, bis er sein Volumen ungefähr verdoppelt hat.',
  durationSeconds: 6,
  keywords: ['gehen lassen', 'aufgehen', 'verdoppelt', 'verdoppeln', 'hefeteig', 'warmen ort', 'gehen'],
  stillTime: 0.82,
  parameters: [
    {
      kind: 'number',
      name: 'minutes',
      label: 'Dauer',
      min: 10,
      max: 720,
      step: 5,
      unit: 'Min',
      defaultValue: 60,
    },
    { kind: 'boolean', name: 'cover', label: 'mit Tuch abdecken', defaultValue: false },
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    return {
      minutes: minutesFromText(text),
      cover: /abgedeckt|zugedeckt|abdecken|zudecken|tuch|folie/.test(lower) ? true : undefined,
    };
  },
  scene(values, t) {
    const minutes = numberValue(values, 'minutes', 60);
    const covered = booleanValue(values, 'cover', false);
    const vessel = bowl(BOWL.cx, BOWL.rim, BOWL.width, BOWL.height);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const rise = ease.inOutSine(segment(t, ...TIME.rise));
    const top = lerp(START_TOP, END_TOP, rise);
    const dough = (at: number): SceneNode =>
      group([shape(doughTop(at), styles.snow(STROKE.outline))], { clip: interiorClip(vessel, 60) });
    const markX = BOWL.cx + BOWL.width / 2 + 10;
    const done = segment(t, ...TIME.done) * (1 - segment(t, 0.88, 0.9));
    return compact([
      ...counter(),
      ...vessel.back,
      reset > 0 ? group([dough(START_TOP)]) : null,
      faded(1 - reset, [dough(top)]),
      ...vessel.front,
      covered ? group(cloth(reset > 0 ? lerp(top, START_TOP, reset) : top)) : null,
      // Marken für Anfang und Ziel
      line(markX - 4, START_TOP, markX + 14, START_TOP, {
        stroke: 'deep',
        strokeWidth: STROKE.detail,
        dash: '3 3',
      }),
      line(markX - 4, END_TOP, markX + 14, END_TOP, {
        stroke: 'deep',
        strokeWidth: STROKE.detail,
        dash: '3 3',
      }),
      line(markX + 5, START_TOP - 3, markX + 5, END_TOP + 3, { stroke: 'deep', strokeWidth: STROKE.fine }),
      timerBadge(BADGE_RIGHT, 24, durationLabel(minutes), t),
      faded(ease.outBack(done), [doneMark(BOWL.cx - BOWL.width / 2 - 2, END_TOP + 4, 1.1)]),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: BOWL.cx, y: START_TOP - 4 },
        { x: BOWL.cx, y: END_TOP - 8 },
      ]),
    ];
  },
});
